/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { memo, useState } from 'react';
import { Typography } from '@mui/material';
import { DataHeading } from '../Charts.styled';
import { SelectMenuItem } from '../Header/Header.styled';
import { SelectWrapper } from '../../../components/common/InputDropdown';
import { ReactComponent as CheckIcon } from '../../../assets/icons/base/check.svg';
import { Box } from '@mui/system';
import { availableAlertsMode, availableIntervals, availableReadsMode } from '../Charts.constants';
import { AggregatedAlertsData,
  AggregatedReadsData,
  AvailableFilters, IntervalType,
  PickedFilters } from '../Charts.types';
import LineGraphChart from './LineGraphChart';

type LineGraphType = {
  readsData: AggregatedReadsData;
  alertsData: AggregatedAlertsData;
  type: 'all' | 'reads' | 'alerts';
  intervalForReads: IntervalType;
  intervalForAlerts: IntervalType;
  setIntervalForReads: React.Dispatch<React.SetStateAction<IntervalType>>;
  setIntervalForAlerts: React.Dispatch<React.SetStateAction<IntervalType>>;
  availableFilters: AvailableFilters;
  pickedFilters: PickedFilters;
  setPickedFilters: React.Dispatch<React.SetStateAction<PickedFilters>>;
  loading: boolean;
  loadingAlerts: boolean;
};
const LineGraph = (props: LineGraphType) => {
  const {
    type, readsData, intervalForReads,
    setIntervalForReads, alertsData, intervalForAlerts,
    setIntervalForAlerts, availableFilters, pickedFilters, setPickedFilters,
    loading, loadingAlerts,
  } = props;

  const [readsMode, setReadsMode] = useState<'site' | 'camera'>('site');
  const [alertsMode, setAlertsMode] = useState<'site' | 'camera' | 'alert'>('alert');

  // Different data source for different mode
  const pickedAlertsData = (alertsMode === 'alert' && alertsData.alertsData)
  || (alertsMode === 'site' && alertsData.sitesData)
  || alertsData.camerasData;

  // Different data source for different mode
  const pickedReadsData = (readsMode === 'site' && readsData.sitesData)
  || readsData.camerasData;

  return (
    <>
      {(type === 'all' || type === 'reads') && (
        <Box>
          <DataHeading>
            <Typography variant="txl" fontWeight="600">
              Reads over time
            </Typography>
            <Box display="flex" gap="16px">
              <Box width="209px">
                <SelectWrapper
                  onChange={(e: any) => setReadsMode(e.target.value)}
                  value={readsMode}
                  renderValue={(selected) => {
                    return <span>{selected === 'site' ? 'By Site' : 'By Camera'}</span>;
                  }}
                  displayEmpty
                >
                  {availableReadsMode.map((arm) => (
                    <SelectMenuItem key={arm.value} value={arm.value}>
                      {arm.name}
                      {arm.value === readsMode && <CheckIcon style={{ height: '20px', width: '20px' }} />}
                    </SelectMenuItem>
                  ))}
                </SelectWrapper>
              </Box>
              <Box width="209px">
                <SelectWrapper
                  onChange={(e: any) => setIntervalForReads(e.target.value)}
                  value={intervalForReads}
                  renderValue={(selected) => {
                    return <span>{availableIntervals.find((d) => d.value === selected)?.name}</span>;
                  }}
                  displayEmpty
                >
                  {availableIntervals.map((arm) => (
                    <SelectMenuItem key={arm.value} value={arm.value}>
                      {arm.name}
                      {arm.value === intervalForReads && <CheckIcon style={{ height: '20px', width: '20px' }} />}
                    </SelectMenuItem>
                  ))}
                </SelectWrapper>
              </Box>
            </Box>
          </DataHeading>
          <LineGraphChart loading={loading} text="Reads" chartData={pickedReadsData} />
        </Box>
      )}
      {(type === 'all' || type === 'alerts') && (
        <Box>
          <DataHeading>
            <Typography variant="txl" fontWeight="600">
              Alerts over time
            </Typography>
            <Box display="flex" gap="16px">
              <Box width="209px">
                <SelectWrapper
                  onChange={(e: any) => setAlertsMode(e.target.value)}
                  value={alertsMode}
                  renderValue={(selected) => {
                    return <span>{availableAlertsMode.find((aam) => aam.value === selected)?.name}</span>;
                  }}
                  displayEmpty
                >
                  {availableAlertsMode.map((arm) => (
                    <SelectMenuItem key={arm.value} value={arm.value}>
                      {arm.name}
                      {arm.value === alertsMode && <CheckIcon style={{ height: '20px', width: '20px' }} />}
                    </SelectMenuItem>
                  ))}
                </SelectWrapper>
              </Box>
              <Box width="209px">
                <SelectWrapper
                  onChange={(e: any) => setPickedFilters({ ...pickedFilters, alerts: e.target.value })}
                  value={pickedFilters.alerts}
                  renderValue={(selected: any) => {
                    return <span>{selected.length} Alarm Classes</span>;
                  }}
                  multiple
                  displayEmpty
                >
                  {availableFilters.alerts.map((arm) => (
                    <SelectMenuItem key={arm.id} value={arm.id}>
                      {arm.name}
                      {pickedFilters.alerts.includes(arm.id) && <CheckIcon style={{ height: '20px', width: '20px' }} />}
                    </SelectMenuItem>
                  ))}
                </SelectWrapper>
              </Box>
              <Box width="209px">
                <SelectWrapper
                  onChange={(e: any) => setIntervalForAlerts(e.target.value)}
                  value={intervalForAlerts}
                  renderValue={(selected) => {
                    return <span>{availableIntervals.find((d) => d.value === selected)?.name}</span>;
                  }}
                  displayEmpty
                >
                  {availableIntervals.map((arm) => (
                    <SelectMenuItem key={arm.value} value={arm.value}>
                      {arm.name}
                      {arm.value === intervalForAlerts && <CheckIcon style={{ height: '20px', width: '20px' }} />}
                    </SelectMenuItem>
                  ))}
                </SelectWrapper>
              </Box>
            </Box>
          </DataHeading>
          <LineGraphChart
            chartData={pickedAlertsData}
            text="Alerts"
            loading={loadingAlerts}
          />
        </Box>
      )}
    </>
  );
};

export default memo(LineGraph);
